import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue, useResetRecoilState } from "recoil";
import { Auth } from "aws-amplify";
import { Bell, ChevronDown, CreditCard, Menu, Settings2 } from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  userData,
  accessToken,
  idToken,
  refreshToken,
  portfolioTreeStore,
  profileImageUrl,
} from "../../store";
import { pendingRequestQueue } from "../../services/axiosInterceptor";
import IdleTimeoutContainer from "../../auth/IdleTimeoutContainer";
import { NotificationContainer } from "../../features/webPush/notificationManager";
import ThemeToggle from "../../features/themeSelect/ThemeToggle";
import { GetRoleLabel } from "../../auth/authUtils";
import {
  isPropertyManager,
  isAsstPropertyManager,
} from "../../auth/accessUtils";
import { useMenuItems } from "./useMenuItems";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import NotificationSheetContent from "./NotificationSheetContent";
import { triggerSnackbar } from "@/components/toastMessage";
import { cn } from "@/lib/utils";
import { GetDateInMMDDYYYY } from "../../utils/formatters/GetDateInMMDDYYYY";
import { useNotification } from "../../features/notifications/useNotification";

const logoutLogoSvg = `
<svg width="60" height="60" viewBox="0 0 120 120" role="img" aria-label="Yvooa Logo" xmlns="http://www.w3.org/2000/svg">
  <title>Yvooa Logo</title>
  <defs>
    <radialGradient id="coinFaceRound" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#e3f2fd" stop-opacity="0.7" />
      <stop offset="50%" stop-color="#1976d2" stop-opacity="0.8" />
      <stop offset="100%" stop-color="#0d47a1" />
    </radialGradient>
    <linearGradient id="yGoldRound" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#F9C642" />
      <stop offset="100%" stop-color="#FFD700" />
    </linearGradient>
  </defs>
  <circle cx="60" cy="60" r="50" fill="url(#coinFaceRound)" />
  <polygon points="53,95 60,30 75,30" fill="#fff" />
  <polygon points="35,30 55,55 50,75" fill="url(#yGoldRound)" />
</svg>
`;
const logoutLogoDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(
  logoutLogoSvg,
)}`;

const roundLogoSvg = `
<svg width="32" height="32" viewBox="0 0 120 120" role="img" aria-label="Yvooa" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="roundGradient" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#e3f2fd" stop-opacity="0.7" />
      <stop offset="50%" stop-color="#1976d2" stop-opacity="0.8" />
      <stop offset="100%" stop-color="#0d47a1" />
    </radialGradient>
    <linearGradient id="yGoldRoundLogo" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#F9C642" />
      <stop offset="100%" stop-color="#FFD700" />
    </linearGradient>
  </defs>
  <circle cx="60" cy="60" r="55" fill="url(#roundGradient)" />
  <polygon points="53,95 60,30 75,30" fill="#fff" />
  <polygon points="35,30 55,55 50,75" fill="url(#yGoldRoundLogo)" />
</svg>
`;

const SidebarStateSync = () => {
  const { open } = useSidebar();

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--app-sidebar-width", open ? "16rem" : "4rem");
    return () => {
      root.style.removeProperty("--app-sidebar-width");
    };
  }, [open]);

  return null;
};

const SidebarInner = () => {
  const userInfo = useRecoilValue(userData);
  const userReset = useResetRecoilState(userData);
  const profileImage = useRecoilValue(profileImageUrl);
  const resetAccessToken = useResetRecoilState(accessToken);
  const resetIdToken = useResetRecoilState(idToken);
  const resetRefreshToken = useResetRecoilState(refreshToken);
  const resetPortfolioTree = useResetRecoilState(portfolioTreeStore);
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen } = useSidebar();

  // Hook reads/writes the shared atom directly — no sync effects needed
  const {
    notifications,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  } = useNotification(userInfo?.ID);

  const [expandedMenus, setExpandedMenus] = useState({});
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const menuItems = useMenuItems();

  useEffect(() => {
    if (userInfo?.ID) {
      fetchNotifications("received");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.ID]);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      pendingRequestQueue.reset();
      // Clear all Recoil atoms
      userReset();
      resetAccessToken();
      resetIdToken();
      resetRefreshToken();
      resetPortfolioTree();

      sessionStorage.clear();

      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      triggerSnackbar("Failed to sign out", "error");
    }
  };

  const confirmLogout = () => {
    setLogoutDialogOpen(true);
  };

  const showYvooaInfo = () => {
    YvooaInfoModal();
  };

  const toggleMenu = (menuId, nextOpen) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: nextOpen ?? !prev[menuId],
    }));
  };

  const isMenuItemActive = (item) => {
    if (item.children) {
      return item.children.some((child) => location.pathname === child.path);
    }
    return location.pathname === item.path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <>
      <SidebarStateSync />
      <Sidebar
        collapsible="icon"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <SidebarHeader>
          <button
            type="button"
            data-cy="yvooaLogo"
            onClick={showYvooaInfo}
            className="flex items-center gap-2 min-w-0"
          >
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(roundLogoSvg)}`}
              alt="Yvooa"
              className="h-7 w-7 flex-shrink-0"
            />
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate group-data-[state=collapsed]/sidebar:hidden">
              Yvooa
            </span>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="group-data-[state=collapsed]/sidebar:hidden">
              <ThemeToggle />
            </div>
            <SidebarTrigger>
              <Menu className="h-4 w-4" />
            </SidebarTrigger>
          </div>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="group-data-[state=collapsed]/sidebar:hidden">
              Navigation
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {menuItems.map((item) => {
                  const hasChildren = item.children && item.children.length > 0;
                  if (!hasChildren) {
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          data-cy={item.id}
                          onClick={() => handleNavigation(item.path)}
                          isActive={isMenuItemActive(item)}
                          title={item.label}
                        >
                          {item.icon}
                          <span className="truncate group-data-[state=collapsed]/sidebar:hidden">
                            {item.label}
                          </span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  return (
                    <Collapsible.Root
                      key={item.id}
                      open={!!expandedMenus[item.id]}
                      onOpenChange={(open) => toggleMenu(item.id, open)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <Collapsible.Trigger asChild>
                          <SidebarMenuButton
                            data-cy={item.id}
                            isActive={isMenuItemActive(item)}
                            title={item.label}
                            className="group/collapsible group/menu-btn"
                          >
                            {item.icon}
                            <span className="truncate group-data-[state=collapsed]/sidebar:hidden">
                              {item.label}
                            </span>
                            <ChevronDown
                              className={cn(
                                "ml-auto h-4 w-4 transition-transform group-data-[state=collapsed]/sidebar:hidden group-data-[state=open]/collapsible:rotate-180",
                                "group-data-[active=true]/menu-btn:text-white dark:group-data-[active=true]/menu-btn:text-blue-300",
                              )}
                            />
                          </SidebarMenuButton>
                        </Collapsible.Trigger>

                        <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down group-data-[state=collapsed]/sidebar:hidden">
                          <SidebarMenuSub>
                            {item.children.map((child) => (
                              <SidebarMenuSubItem key={child.path}>
                                <SidebarMenuSubButton
                                  data-cy={child.id}
                                  onClick={() => handleNavigation(child.path)}
                                  isActive={location.pathname === child.path}
                                >
                                  {child.label}
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </Collapsible.Content>
                      </SidebarMenuItem>
                    </Collapsible.Root>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setNotifOpen(true)}
                isActive={location.pathname === "/notifications"}
                title="Notifications"
                className="relative"
              >
                <Bell className="h-5 w-5" />
                <span className="truncate group-data-[state=collapsed]/sidebar:hidden">
                  Notifications
                </span>
                {notifications.some((notif) => !notif.isRead) && (
                  <span className="absolute right-3 h-2 w-2 rounded-full bg-red-500 group-data-[state=collapsed]/sidebar:right-2" />
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
            {!["Property Manager", "Assistant Property Manager"].includes(
              userInfo?.userRole,
            ) && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => handleNavigation("/subscription/Details")}
                  isActive={location.pathname === "/subscription/Details"}
                  title="Subscription"
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="truncate group-data-[state=collapsed]/sidebar:hidden">
                    Subscription
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => handleNavigation("/provisioning")}
                isActive={location.pathname.startsWith("/provisioning")}
                title="Provisioning"
              >
                <Settings2 className="h-5 w-5" />
                <span className="truncate group-data-[state=collapsed]/sidebar:hidden">
                  Provisioning
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>

          <div className="mt-3 border-t border-gray-200 pt-3 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarImage src={profileImage} alt="Profile" />
                <AvatarFallback>
                  {userInfo?.firstName?.[0]}
                  {userInfo?.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 group-data-[state=collapsed]/sidebar:hidden">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {userInfo?.firstName} {userInfo?.lastName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {userInfo?.userRole || GetRoleLabel(userInfo?.userRole)}
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 break-all group-data-[state=collapsed]/sidebar:hidden">
              {userInfo?.email}
            </p>
            <div className="mt-3 flex gap-2 group-data-[state=collapsed]/sidebar:hidden">
              <button
                data-cy="myProfile"
                onClick={() => handleNavigation("/myprofile")}
                className="flex-1 rounded-md bg-blue-600 px-2 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
              >
                My Profile
              </button>
              <button
                data-cy="logOut"
                onClick={confirmLogout}
                className="flex-1 rounded-md bg-red-100 px-2 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
              >
                Logout
              </button>
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* Notifications Sheet */}
      <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
        <SheetContent
          side="right"
          className="w-[400px] sm:w-[480px] p-0 flex flex-col"
        >
          <NotificationSheetContent
            notifications={notifications}
            onMarkAsRead={markNotificationAsRead}
            onMarkAllAsRead={() => markAllNotificationsAsRead()}
            onClose={() => setNotifOpen(false)}
          />
        </SheetContent>
      </Sheet>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent
          showCloseButton={false}
          className="w-[92vw] max-w-[400px] p-8 text-center overflow-hidden rounded-xl"
        >
          {/* Centered vertical layout */}
          <div className="flex flex-col items-center gap-5">
            {/* Logo */}
            <div className="w-[80px] h-[80px] rounded-full overflow-hidden shadow-md ring-4 ring-blue-100">
              <img
                src={logoutLogoDataUrl}
                alt="Yvooa"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white tracking-tight">
                Confirm Logout
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                Are you sure you want to log out? You will need to sign in again
                to continue.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <button
                data-cy="confirmLogout"
                onClick={async () => {
                  setLogoutDialogOpen(false);
                  await handleLogout();
                }}
                className="flex-1 py-2.5 px-4 bg-[#006DBA] hover:bg-[#025fa1] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-[#006DBA] focus:ring-offset-2"
              >
                Log Out
              </button>
              <button
                data-cy="cancelLogout"
                onClick={() => setLogoutDialogOpen(false)}
                className="flex-1 py-2.5 px-4 bg-white dark:bg-transparent border border-[#006DBA] text-[#006DBA] text-sm font-semibold rounded-lg hover:bg-[#006DBA] hover:text-white dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#006DBA] focus:ring-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <IdleTimeoutContainer handleLogout={handleLogout} />
    </>
  );
};

const SidebarNavbar = () => (
  <>
    <NotificationContainer />
    <SidebarProvider defaultOpen={false}>
      <SidebarInner />
    </SidebarProvider>
  </>
);

export default SidebarNavbar;
